import React from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Grid,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  useMediaQuery,
  Divider,
  Box,
} from "@material-ui/core";
import { grey, green, deepOrange, amber } from "@material-ui/core/colors";
import DoneIcon from "@material-ui/icons/Done";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { makeStyles } from "@material-ui/core/styles";
import { AclType, InventoryStatus } from "../../../utils/constants";
import { isAuthorized } from "../../../utils/appUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import {
  isRailShipmentInTrackArea,
  isTruckToRail,
} from "../../../utils/locationUtils";
import PlaylistAddOutlinedIcon from "@material-ui/icons/PlaylistAddOutlined";
import { useInboundContext } from "./context";
import { format } from "date-fns";
import PendingApprovalIcon from "@material-ui/icons/PriorityHigh";

const useStyles = makeStyles((theme) => ({
  actions: {
    display: "flex",
    justifyContent: "space-between",
  },
  avatar: {
    marginTop: theme.spacing(1),
  },
  deepOrange: {
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
  },
  progress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 300,
    width: "100%",
  },
  dialogActions: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    margin: theme.spacing(1),
  },
  actionButton: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  green: {
    color: theme.palette.getContrastText(green[200]),
    backgroundColor: green[200],
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  amber: {
    color: theme.palette.getContrastText(amber[500]),
    backgroundColor: amber[500],
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  unprocessedItem: {
    color: theme.palette.getContrastText(grey[300]),
    backgroundColor: grey[300],
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  Toolbar: {
    display: "flex",
    alignItems: "center",
  },
}));

const InboundInventoryItems = () => {
  const { state, dispatch, notify } = useInboundContext();
  const { currentInventory } = state;
  const lastSelectedAisle = useSelector(
    (state) => state.data.lastSelectedAisle,
  );
  const classes = useStyles();

  const handleAdd = async () => {
    // await getAisles(currentInventory.zoneId);
    const memorizedAisle = lastSelectedAisle?.aisle;
    let selectedAisle;
    if (memorizedAisle?.id) selectedAisle = memorizedAisle;
    const payload = {
      disableSelection: false,
      selectedAisle,
      selectedInventoryItem: {},
    };
    dispatch({ type: "INIT_ADD_INVENTORY_ITEM", payload });
  };

  // const isSelectionDisabled = () => {
  //     return currentInventory.reloadYardCommodity?.commodity?.locationLoadType !== LocationLoadType.OTHER
  // }

  const handleOpenInventoryItem = async (inventoryItem) => {
    // if (inventoryItem.status === InventoryStatus.UNLOADED || inventoryItem.status === InventoryStatus.STORED) return;
    // dispatch({type: "SET_LOADING", payload: true});
    // await getAisles(inventoryItem.zoneId);
    // const disableSelection = isSelectionDisabled();
    debugger;
    // An item on the Unspotted lane has no Location record, so there is no
    // aisle map to open. Say so instead of routing into an empty map view.
    if (!inventoryItem?.location) {
      notify(
        "Railcar is not spotted. Spot it in Track Management to place this item.",
        "warning",
      );
      return;
    }
    dispatch({
      type: "SELECT_INVENTORY_ITEM",
      payload: {
        currentInventory,
        selectedInventoryItem: inventoryItem,
        disableSelection: true,
        selectedLocation: inventoryItem.location,
        origInventoryItem: { ...inventoryItem },
      },
    });
  };

  // An item on the Unspotted lane has no Location record, so there is no aisle
  // map to open — the row stays greyed out until the railcar is spotted.
  const isRowDisabled = (inventoryItem) => !inventoryItem?.location;

  const getAvatarClassName = (inventoryItem) => {
    if (inventoryItem.isDamaged) {
      return classes.amber;
    }
    if (
      inventoryItem.status === InventoryStatus.UNLOADED ||
      inventoryItem.status === InventoryStatus.STORED ||
      inventoryItem.status === InventoryStatus.AGGREGATED
    )
      return classes.green;
    return classes.unprocessedItem;
  };

  const getIcon = (inventoryItem) => {
    if (inventoryItem.isDamaged) {
      return <PendingApprovalIcon fontSize="inherit" />;
    }
    if (
      inventoryItem.status === InventoryStatus.UNLOADED ||
      inventoryItem.status === InventoryStatus.STORED ||
      inventoryItem.status === InventoryStatus.AGGREGATED
    )
      return <DoneIcon fontSize="small" />;
    return <CloseOutlinedIcon fontSize="small" />;
  };

  return (
    <>
      <Grid item xs={12}>
        <InventoryItemsToolbar
          handleAddInventoryItem={handleAdd}
          classes={classes}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
        <List>
          {currentInventory.inventoryItems?.items.map((itm, idx) => (
            <div key={itm.id}>
              <ListItem
                disabled={isRowDisabled(itm)}
                onClick={() => handleOpenInventoryItem(itm)}
              >
                <ListItemAvatar>
                  <Avatar className={getAvatarClassName(itm)}>
                    {getIcon(itm)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${itm.locationFullName + " | "}${
                    itm.quantityOnHand
                  } ${getUnitName(currentInventory.inventoryUOM)}`}
                  primaryTypographyProps={
                    itm.hasException ? { color: "error" } : null
                  }
                  secondary={
                    itm.unloadedBy &&
                    `${itm.unloadedBy} - ${format(
                      new Date(itm.unloadedAt),
                      "MM-dd hh:mm a",
                    )}`
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    disabled={isRowDisabled(itm)}
                    onClick={() => handleOpenInventoryItem(itm)}
                  >
                    <KeyboardArrowRightIcon fontSize="large" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {idx < currentInventory.inventoryItems.items.length - 1 ? (
                <Divider />
              ) : null}
            </div>
          ))}
        </List>
      </Grid>
    </>
  );
};

const InventoryItemsToolbar = ({ handleAddInventoryItem, classes }) => {
  const { state } = useInboundContext();
  const { currentInventory, currentShipment } = state;
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const isAddDisabled = () => {
    if (
      currentShipment.containerDrop &&
      currentInventory?.inventoryItems?.items?.length > 0
    ) {
      return true;
    }
    if (
      !isAuthorized(AclType.ASSIGN_LOCATION) ||
      currentInventory.status === InventoryStatus.UNLOADED ||
      (isTruckToRail(currentInventory.areaId, currentShipment) &&
        !currentShipment.containerDrop) || // receiving railcar assigned by manager
      isRailShipmentInTrackArea(currentInventory.areaId, currentShipment) // Rail shipment track location is set in Track Management
    )
      return true;

    return false;
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      componennt="span"
    >
      <Typography className={classes.title} variant="h6" component="span">
        Locations
      </Typography>
      <Button
        onClick={() => handleAddInventoryItem()}
        startIcon={<PlaylistAddOutlinedIcon />}
        disabled={isAddDisabled()}
        variant="contained"
        color="primary"
      >
        {xsDown ? "Add" : "Add Location"}
      </Button>
    </Box>
  );
};

export default InboundInventoryItems;
